 
import { DataKey } from '../lib/model';
import {
	useEffect,
	useState,
	createContext,
	ReactNode,
	useContext
} from 'react';
import { useData } from './DataProvider';
import { useTheme } from '@mui/joy';
import { useRoute } from './RouteProvider';
import { max } from 'd3-array';
import { STATES_WITH_EDGE_CASES } from '../lib/const';
import { useEdgeCases } from './EdgeCaseProvider';

export interface ChartSectionContextInterface {
	chartData: {
		group: string;
		partyLabel: string;
		chamber: string;
		label: string;
		value: number;
		numCand: number;
		color: string;
		height: string;
	}[][];
	maxValue: number;
	maxInParty: { [x: string]: number };
}

type dataObjArray = {
	group: string;
	partyLabel: string;
	chamber: string;
	label: string;
	value: number;
	numCand: number;
	color: string;
	height: string;
};

// eslint-disable-next-line react-refresh/only-export-components
export const CHART_SECTION_CONTEXT_DEFAULT: ChartSectionContextInterface = {
	chartData: [],
	maxValue: 0,
	maxInParty: { rep: 0, dem: 0, ind: 0 }
};

const ChartSectionContext = createContext(CHART_SECTION_CONTEXT_DEFAULT);

// eslint-disable-next-line react-refresh/only-export-components
export function useChartSectionData() {
	return useContext<ChartSectionContextInterface>(ChartSectionContext);
}
interface Row {
	[x: string]: string;
}
interface IProps {
	topic: string;
	children: ReactNode;
	page: string;
}
export default function ChartSectionProvider({
	topic,
	children,
	page
}: IProps) {
	const theme = useTheme();
	const {
		stateHouse10Data,
		stateSenate10Data,
		usHouse10Data,
		usSenate10Data,
		usSenateData,
		usHouseData
	} = useData();
	const { selectedState, congressTab, election, seat, raceFilter } = useRoute();
	const [chartData, setChartData] = useState<dataObjArray[][]>(
		CHART_SECTION_CONTEXT_DEFAULT.chartData
	);
	const [maxValue, setMaxValue] = useState<number>(
		CHART_SECTION_CONTEXT_DEFAULT.maxValue
	);
	const [maxInParty, setMaxInParty] = useState<{ [x: string]: number }>(
		CHART_SECTION_CONTEXT_DEFAULT.maxInParty
	);

	const hasEdgeCases =
		STATES_WITH_EDGE_CASES.includes(selectedState as string) &&
		page === 'state';

	const { listOfOffices, edgeCaseDataSorted } = useEdgeCases();

	const repPartyVals = [
		'Republican',
		'Republican Party',
		'Republican party',
		'REPUBLICAN PARTY',
		'REPUBLICAN'
	];
	const demPartyVals = [
		'Democratic',
		'Democratic Party',
		'Democratic party',
		'DEMOCRATIC PARTY',
		'DEMOCRATIC'
	];

	useEffect(() => {
		const compileData = (
			data: { [x: string]: string }[],
			key: string,
			edge: boolean = false
		) => {
			const menDem = data
				?.filter(
					(d: { [x: string]: string }) =>
						demPartyVals.includes(d[DataKey.Party]) &&
						d[DataKey.Gender] === 'Man'
				)
				.filter(
					(d: { [x: string]: string }) =>
						d[seat[0]] === '1' && d[key] !== 'NA' && d[key] !== 'na'
				);

			const avgMenDem = menDem?.length
				? menDem.reduce(
						(total: number, d: { [x: string]: string }) =>
							total + Number(d[key] === 'NA' || d[key] === 'na' ? 0 : d[key]),
						0
				  ) / menDem?.length
				: 0;

			const womenDem = data
				?.filter(
					(d: { [x: string]: string }) =>
						demPartyVals.includes(d[DataKey.Party]) &&
						d[DataKey.Gender] === 'Woman'
				)
				.filter(
					(d: { [x: string]: string }) =>
						d[seat[0]] === '1' && d[key] !== 'NA' && d[key] !== 'na'
				);

			const avgWomenDem = womenDem?.length
				? womenDem.reduce(
						(total: number, d: { [x: string]: string }) =>
							total + Number(d[key] === 'NA' || d[key] === 'na' ? 0 : d[key]),
						0
				  ) / womenDem.length
				: 0;

			const womenRep = data
				?.filter(
					(d: { [x: string]: string }) =>
						repPartyVals.includes(d[DataKey.Party]) &&
						d[DataKey.Gender] === 'Woman'
				)
				.filter(
					(d: { [x: string]: string }) =>
						d[seat[0]] === '1' && d[key] !== 'NA' && d[key] !== 'na'
				);

			const avgWomenRep = womenRep?.length
				? womenRep.reduce(
						(total: number, d: { [x: string]: string }) =>
							total + Number(d[key] === 'NA' || d[key] === 'na' ? 0 : d[key]),
						0
				  ) / womenRep?.length
				: 0;

			const menRep = data
				?.filter(
					(d: { [x: string]: string }) =>
						repPartyVals.includes(d[DataKey.Party]) &&
						d[DataKey.Gender] === 'Man'
				)
				.filter(
					(d: { [x: string]: string }) =>
						d[seat[0]] === '1' && d[key] !== 'NA' && d[key] !== 'na'
				);

			const avgMenRep = menRep?.length
				? menRep.reduce(
						(total: number, d: { [x: string]: string }) =>
							total + Number(d[key] === 'NA' || d[key] === 'na' ? 0 : d[key]),
						0
				  ) / menRep?.length
				: 0;

			const womenInd = data
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.Party] === 'Independent' && d[DataKey.Gender] === 'Woman'
				)
				.filter(
					(d: { [x: string]: string }) =>
						d[seat[0]] === '1' && d[key] !== 'NA' && d[key] !== 'na'
				);

			const avgWomenInd = womenInd?.length
				? womenInd.reduce(
						(total: number, d: { [x: string]: string }) =>
							total + Number(d[key] === 'NA' || d[key] === 'na' ? 0 : d[key]),
						0
				  ) / womenInd?.length
				: 0;

			const menInd = data
				?.filter(
					(d: { [x: string]: string }) =>
						d[DataKey.Party] === 'Independent' && d[DataKey.Gender] === 'Man'
				)
				.filter(
					(d: { [x: string]: string }) =>
						d[seat[0]] === '1' && d[key] !== 'NA' && d[key] !== 'na'
				);

			const avgMenInd = menInd?.length
				? menInd.reduce(
						(total: number, d: { [x: string]: string }) =>
							total + Number(d[key] === 'NA' || d[key] === 'na' ? 0 : d[key]),
						0
				  ) / menInd?.length
				: 0;

			const groupData = [menRep, womenRep, menDem, womenDem, menInd, womenInd];

			const averages: number[] = [
				avgMenRep,
				avgWomenRep,
				avgMenDem,
				avgWomenDem,
				avgMenInd,
				avgWomenInd
			];

			const labels = [
				'Men',
				'Women',
				'Men',
				'Women',
				'Men',
				'Women',
				'Men',
				'Women',
				'Men',
				'Women',
				'Men',
				'Women'
			];

			const dataObj = averages
				.map((avg, i) => {
					return groupData[i]?.length
						? {
								group: `${labels[i]}${i <= 1 ? 'GOP' : i <= 3 ? 'DEM' : 'IND'}`,
								chamber: edge
									? groupData[i][0][DataKey.OfficeSought]
									: groupData[i][0].chamber,
								partyLabel: i <= 1 ? 'GOP' : i <= 3 ? 'DEM' : 'IND',
								label: labels[i],
								value: avg,
								numCand: groupData[i].length,
								height: 'wide',
								color:
									i === 0
										? theme.colorSchemes.light.palette
												.categoryPartyRepublicanMen.solidBg
										: i === 1
										? theme.colorSchemes.light.palette
												.categoryPartyRepublicanWomen.solidBg
										: i === 2
										? theme.colorSchemes.light.palette.categoryPartyDemocratMen
												.solidBg
										: i === 3
										? theme.colorSchemes.light.palette
												.categoryPartyDemocratWomen.solidBg
										: i === 4
										? theme.colorSchemes.light.palette
												.categoryPartyIndependentMen.solidBg
										: theme.colorSchemes.light.palette
												.categoryPartyIndependentWomen.solidBg
						  }
						: null;
				})
				.filter((item) => item);

			return dataObj as dataObjArray[];
		};

		const compileDataByRace = (
			data: { [x: string]: string }[],
			key: string,
			edge: boolean = false
		) => {
			const menDemAll: Row[] = [];

			const womDemAllB: Row[] = [],
				womDemAllW: Row[] = [],
				womDemAllL: Row[] = [],
				womDemAllAP: Row[] = [],
				womDemAllMENA: Row[] = [],
				womDemAllNatAm: Row[] = [];

			const menRepAll: Row[] = [];

			const womRepAllB: Row[] = [],
				womRepAllW: Row[] = [],
				womRepAllL: Row[] = [],
				womRepAllAP: Row[] = [],
				womRepAllMENA: Row[] = [],
				womRepAllNatAm: Row[] = [];

			const menIndAll: Row[] = [];
			const womIndAllB: Row[] = [],
				womIndAllW: Row[] = [],
				womIndAllL: Row[] = [],
				womIndAllAP: Row[] = [],
				womIndAllMENA: Row[] = [],
				womIndAllNatAm: Row[] = [];

			data?.forEach((d: { [x: string]: string }) => {
				if (
					demPartyVals.includes(d[DataKey.Party]) &&
					d[DataKey.Gender] === 'Man'
				) {
					menDemAll.push(d);
				} else if (
					demPartyVals.includes(d[DataKey.Party]) &&
					d[DataKey.Gender] === 'Woman'
				) {
					if (d[DataKey.CandRace] === 'B') womDemAllB.push(d);
					else if (d[DataKey.CandRace] === 'W') womDemAllW.push(d);
					else if (d[DataKey.CandRace] === 'L' || d[DataKey.CandRace] === 'H')
						womDemAllL.push(d);
					else if (d[DataKey.CandRace] === 'AP') womDemAllAP.push(d);
					else if (d[DataKey.CandRace] === 'MENA') womDemAllMENA.push(d);
					else if (d[DataKey.CandRace] === 'NatAm') womDemAllNatAm.push(d);
				} else if (
					repPartyVals.includes(d[DataKey.Party]) &&
					d[DataKey.Gender] === 'Man'
				) {
					menRepAll.push(d);
				} else if (
					repPartyVals.includes(d[DataKey.Party]) &&
					d[DataKey.Gender] === 'Woman'
				) {
					if (d[DataKey.CandRace] === 'B') womRepAllB.push(d);
					else if (d[DataKey.CandRace] === 'W') womRepAllW.push(d);
					else if (d[DataKey.CandRace] === 'L' || d[DataKey.CandRace] === 'H')
						womRepAllL.push(d);
					else if (d[DataKey.CandRace] === 'AP') womRepAllAP.push(d);
					else if (d[DataKey.CandRace] === 'MENA') womRepAllMENA.push(d);
					else if (d[DataKey.CandRace] === 'NatAm') womRepAllNatAm.push(d);
				} else if (
					d[DataKey.Party] === 'Independent' &&
					d[DataKey.Gender] === 'Man'
				) {
					menIndAll.push(d);
				} else if (
					d[DataKey.Party] === 'Independent' &&
					d[DataKey.Gender] === 'Woman'
				) {
					if (d[DataKey.CandRace] === 'B') womIndAllB.push(d);
					else if (d[DataKey.CandRace] === 'W') womIndAllW.push(d);
					else if (d[DataKey.CandRace] === 'L' || d[DataKey.CandRace] === 'H')
						womIndAllL.push(d);
					else if (d[DataKey.CandRace] === 'AP') womIndAllAP.push(d);
					else if (d[DataKey.CandRace] === 'MENA') womIndAllMENA.push(d);
					else if (d[DataKey.CandRace] === 'NatAm') womIndAllNatAm.push(d);
				}
			});

			const filterForSeat = (data: { [x: string]: string }[], key: string) => {
				return data?.filter(
					(d: { [x: string]: string }) =>
						d[seat[0]] === '1' && d[key] !== 'NA' && d[key] !== 'na'
				);
			};

			const findAverage = (data: { [x: string]: string }[], key: string) => {
				return data.length
					? data.reduce((total: number, d: { [x: string]: string }) => {
							return (
								total + Number(d[key] === 'NA' || d[key] === 'na' ? 0 : d[key])
							);
					  }, 0) / data?.length
					: 0;
			};

			const menRep = filterForSeat(menRepAll, key);
			const avgMenRep = findAverage(menRep, key) as unknown as number;
			const womRepB = filterForSeat(womRepAllB, key);
			const avgWomRepB = findAverage(womRepB, key) as unknown as number;
			const womRepW = filterForSeat(womRepAllW, key);
			const avgWomRepW = findAverage(womRepW, key) as unknown as number;
			const womRepL = filterForSeat(womRepAllL, key);
			const avgWomRepL = findAverage(womRepL, key) as unknown as number;
			const womRepAP = filterForSeat(womRepAllAP, key);
			const avgWomRepAP = findAverage(womRepAP, key) as unknown as number;
			const womRepMENA = filterForSeat(womRepAllMENA, key);
			const avgWomRepMENA = findAverage(womRepMENA, key) as unknown as number;
			const womRepNatAm = filterForSeat(womRepAllNatAm, key);
			const avgWomRepNatAm = findAverage(womRepNatAm, key) as unknown as number;

			const menDem = filterForSeat(menDemAll, key);
			const avgMenDem = findAverage(menDem, key) as unknown as number;

			const womDemB = filterForSeat(womDemAllB, key);
			const avgWomDemB = findAverage(womDemB, key) as unknown as number;
			const womDemW = filterForSeat(womDemAllW, key);
			const avgWomDemW = findAverage(womDemW, key) as unknown as number;
			const womDemL = filterForSeat(womDemAllL, key);
			const avgWomDemL = findAverage(womDemL, key) as unknown as number;
			const womDemAP = filterForSeat(womDemAllAP, key);
			const avgWomDemAP = findAverage(womDemAP, key) as unknown as number;
			const womDemMENA = filterForSeat(womDemAllMENA, key);
			const avgWomDemMENA = findAverage(womDemMENA, key) as unknown as number;
			const womDemNatAm = filterForSeat(womDemAllNatAm, key);
			const avgWomDemNatAm = findAverage(womDemNatAm, key) as unknown as number;

			const menInd = filterForSeat(menIndAll, key);
			const avgMenInd = findAverage(menInd, key) as unknown as number;

			const womIndB = filterForSeat(womIndAllB, key);
			const avgWomIndB = findAverage(womIndB, key) as unknown as number;
			const womIndW = filterForSeat(womIndAllW, key);
			const avgWomIndW = findAverage(womIndW, key) as unknown as number;
			const womIndL = filterForSeat(womIndAllL, key);
			const avgWomIndL = findAverage(womIndL, key) as unknown as number;
			const womIndAP = filterForSeat(womIndAllAP, key);
			const avgWomIndAP = findAverage(womIndAP, key) as unknown as number;
			const womIndMENA = filterForSeat(womIndAllMENA, key);
			const avgWomIndMENA = findAverage(womIndMENA, key) as unknown as number;
			const womIndNatAm = filterForSeat(womIndAllNatAm, key);
			const avgWomIndNatAm = findAverage(womIndNatAm, key) as unknown as number;

			const groupData = [
				menRep,
				womRepAP,
				womRepB,
				womRepL,
				womRepMENA,
				womRepNatAm,
				womRepW,

				menDem,
				womDemAP,
				womDemB,
				womDemL,
				womDemMENA,
				womDemNatAm,
				womDemW,

				menInd,
				womIndAP,
				womIndB,
				womIndL,
				womIndMENA,
				womIndNatAm,
				womIndW
			];

			const averages: number[] = [
				avgMenRep,
				avgWomRepAP,
				avgWomRepB,
				avgWomRepL,
				avgWomRepMENA,
				avgWomRepNatAm,
				avgWomRepW,

				avgMenDem,
				avgWomDemAP,
				avgWomDemB,
				avgWomDemL,
				avgWomDemMENA,
				avgWomDemNatAm,
				avgWomDemW,

				avgMenInd,
				avgWomIndAP,
				avgWomIndB,
				avgWomIndL,
				avgWomIndMENA,
				avgWomIndNatAm,
				avgWomIndW
			];

			const labels = [
				'Men, All Races',
				'AAPI Women',
				'Black Women',
				'Latina Women',
				'MENA Women',
				'NatAm Women',
				'White Women',
				'Men, All Races',
				'AAPI Women',
				'Black Women',
				'Latina Women',
				'MENA Women',
				'NatAm Women',
				'White Women',
				'Men, All Races',
				'AAPI Women',
				'Black Women',
				'Latina Women',
				'MENA Women',
				'NatAm Women',
				'White Women'
			];

			const dataObj = averages
				.map((avg, i) => {
					return groupData[i].length
						? {
								group: `${labels[i]}${i <= 6 ? 'GOP' : 'DEM'}`,
								chamber: edge
									? groupData[i][0][DataKey.OfficeSought]
									: groupData[i][0][DataKey.Chamber],
								partyLabel: i <= 6 ? 'GOP' : i <= 13 ? 'DEM' : 'IND',
								label: labels[i],
								value: avg,
								numCand: groupData[i].length,
								color:
									i === 0
										? theme.colorSchemes.light.palette
												.categoryPartyRepublicanMen.solidBg
										: i <= 6
										? theme.colorSchemes.light.palette
												.categoryPartyRepublicanWomen.solidBg
										: i === 7
										? theme.colorSchemes.light.palette.categoryPartyDemocratMen
												.solidBg
										: i <= 13
										? theme.colorSchemes.light.palette
												.categoryPartyDemocratWomen.solidBg
										: i === 14
										? theme.colorSchemes.light.palette
												.categoryPartyIndependentMen.solidBg
										: theme.colorSchemes.light.palette
												.categoryPartyIndependentWomen.solidBg
						  }
						: null;
				})
				.filter((item) => item);

			return dataObj as dataObjArray[];
		};

		const makeDataObjArray = () => {
			if (page === 'state' && election === 'state' && hasEdgeCases) {
				if (raceFilter[0] === 'true') {
					return listOfOffices
						?.map((office) => {
							return compileDataByRace(edgeCaseDataSorted[office], topic, true);
						})
						.concat([
							compileDataByRace(
								stateSenate10Data as { [x: string]: string }[],
								topic
							),
							compileDataByRace(
								stateHouse10Data as { [x: string]: string }[],
								topic
							)
						]) as dataObjArray[][];
				} else
					return listOfOffices
						?.map((office) => {
							return compileData(edgeCaseDataSorted[office], topic, true);
						})
						.concat([
							compileData(
								stateSenate10Data as { [x: string]: string }[],
								topic
							),
							compileData(stateHouse10Data as { [x: string]: string }[], topic)
						]);
			} else if (page === 'state' && election === 'state') {
				if (raceFilter[0] === 'true') {
					return [
						compileDataByRace(
							stateSenate10Data as { [x: string]: string }[],
							topic
						),
						compileDataByRace(
							stateHouse10Data as { [x: string]: string }[],
							topic
						)
					];
				} else
					return [
						compileData(stateSenate10Data as { [x: string]: string }[], topic),
						compileData(stateHouse10Data as { [x: string]: string }[], topic)
					];
			} else if (
				page === 'state' &&
				election === 'congress' &&
				congressTab === 0
			) {
				return [
					compileData(usSenate10Data as { [x: string]: string }[], topic),
					compileDataByRace(usSenate10Data as { [x: string]: string }[], topic)
				];
			} else if (
				page === 'state' &&
				election === 'congress' &&
				congressTab === 1
			) {
				return [
					compileData(usHouse10Data as { [x: string]: string }[], topic),
					compileDataByRace(usHouse10Data as { [x: string]: string }[], topic)
				];
			} else if (page === 'national' && congressTab === 0) {
				return [
					compileData(usSenateData as { [x: string]: string }[], topic),
					compileDataByRace(usSenateData as { [x: string]: string }[], topic)
				];
			} else if (page === 'national' && congressTab === 1) {
				return [
					compileData(usHouseData as { [x: string]: string }[], topic),
					compileDataByRace(usHouseData as { [x: string]: string }[], topic)
				];
			} else return [];
		};

		const dataObjArray = makeDataObjArray() as dataObjArray[][];
		setChartData(dataObjArray);

		const findMaxValue = () => {
			const vals: number[] = [];
			dataObjArray.forEach((arr) => {
				arr.forEach((d: { value: number }) => vals.push(d.value));
			});
			 
			return max(vals) as number;
		};
		const maxVal: number = findMaxValue();

		setMaxValue(maxVal);
		const findMaxNumParty: () => { [x: string]: number } = () => {
			const reps: number[] = [];
			const dems: number[] = [];
			const inds: number[] = [];
			dataObjArray.forEach((arr) => {
				const rep: number = arr.filter(
					({ partyLabel }) => partyLabel === 'GOP'
				).length;
				reps.push(rep);

				const dem: number = arr.filter(
					({ partyLabel }) => partyLabel === 'DEM'
				).length;
				dems.push(dem);
				const ind: number = arr.filter(
					({ partyLabel }) => partyLabel === 'IND'
				).length;
				inds.push(ind);
			});
			return {
				 
				reps: max(reps) as number,
				 
				dems: max(dems) as number,
				 
				inds: max(inds) as number
			};
		};
		const maxNum: { [x: string]: number } = findMaxNumParty();
		setMaxInParty(maxNum);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		stateHouse10Data,
		stateSenate10Data,
		edgeCaseDataSorted,
		usSenate10Data,
		usHouse10Data,
		usSenateData,
		usHouseData,
		topic,
		//office,
		congressTab,
		election,
		seat,
		raceFilter
	]);

	return (
		<ChartSectionContext.Provider
			value={{
				chartData,
				maxValue,
				maxInParty
			}}
		>
			{children}
		</ChartSectionContext.Provider>
	);
}
